/**
 * AI Command Center - Tab Component (Refactored)
 * 
 * Central de agentes de IA para monitoramento e automacao operacional.
 * Parte do Admin Master do Kaiso System.
 * 
 * This file was refactored to improve maintainability.
 * Individual sections are now in separate component files.
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Bot, AlertTriangle, CheckCircle2, RefreshCcw, ChevronRight, Play, 
  Filter, Circle, HeartPulse, TrendingUp
} from "lucide-react";

// Import refactored components
import {
  AgentIcons, AgentAvatars, PriorityColors, StatusColors, ModeLabels,
  AutoRepairSection,
  AutoRepairHistoryDashboard,
  TaskDetailDialog,
  JarvisDetails,
  MetricsDashboard,
  AlertsDashboard
} from "./components/command-center";

export default function AICommandCenterTab({ adminAxios }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], counts: { total: 0, critical: 0, warning: 0 } });
  const [autoRepairStats, setAutoRepairStats] = useState(null);
  const [autoRepairAttempts, setAutoRepairAttempts] = useState([]);
  const [autoRepairHistory, setAutoRepairHistory] = useState(null);
  const [historyPeriod, setHistoryPeriod] = useState(7);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filters, setFilters] = useState({ agent: "", status: "", priority: "" });
  const [activeTab, setActiveTab] = useState("overview");

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminAxios.get("/command-center/dashboard");
      setDashboard(res.data);
    } catch (err) {
      toast.error("Erro ao carregar dashboard");
    }
  }, [adminAxios]);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await adminAxios.get("/command-center/metrics?days=7");
      setMetrics(res.data);
    } catch (err) {
      console.error("Erro ao carregar metricas:", err);
    }
  }, [adminAxios]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await adminAxios.get("/command-center/alerts?limit=50");
      setAlerts(res.data);
    } catch (err) {
      console.error("Erro ao carregar alertas:", err);
    }
  }, [adminAxios]);

  const fetchAutoRepairStats = useCallback(async () => {
    try {
      const res = await adminAxios.get("/command-center/auto-repair/stats?hours=24");
      setAutoRepairStats(res.data.stats);
    } catch (err) {
      console.error("Erro ao carregar stats de auto-repair:", err);
    }
  }, [adminAxios]);

  const fetchAutoRepairAttempts = useCallback(async () => {
    try {
      const res = await adminAxios.get("/command-center/auto-repair/attempts?limit=50");
      setAutoRepairAttempts(res.data.attempts || []);
    } catch (err) {
      console.error("Erro ao carregar historico de auto-repair:", err);
    }
  }, [adminAxios]);

  const fetchAutoRepairHistory = useCallback(async (days = 7) => {
    try {
      const res = await adminAxios.get(`/command-center/auto-repair/history?days=${days}&granularity=day`);
      setAutoRepairHistory(res.data);
    } catch (err) {
      console.error("Erro ao carregar historico de graficos:", err);
    }
  }, [adminAxios]);

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.agent) params.append("agent", filters.agent);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      params.append("limit", "50");
      
      const res = await adminAxios.get(`/command-center/tasks?${params}`);
      setTasks(res.data.tasks || []);
    } catch (err) {
      toast.error("Erro ao carregar tarefas");
    }
  }, [adminAxios, filters]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(), 
        fetchTasks(), 
        fetchMetrics(), 
        fetchAlerts(),
        fetchAutoRepairStats(),
        fetchAutoRepairAttempts(),
        fetchAutoRepairHistory(historyPeriod)
      ]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchTasks]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(), 
      fetchTasks(), 
      fetchAutoRepairStats(), 
      fetchAutoRepairAttempts()
    ]);
    setLoading(false);
    toast.success("Dados atualizados");
  };

  const handleRunScan = async (agent = null) => {
    try {
      const url = agent ? `/command-center/scan?agent=${agent}` : "/command-center/scan";
      const res = await adminAxios.post(url);
      toast.success(`Scan concluido: ${res.data.tasks_created} tarefas criadas`);
      await fetchTasks();
    } catch (err) {
      toast.error("Erro ao executar scan");
    }
  };

  const handleViewTask = async (taskId) => {
    try {
      const res = await adminAxios.get(`/command-center/tasks/${taskId}`);
      setSelectedTask(res.data);
    } catch (err) {
      toast.error("Erro ao carregar detalhes da tarefa");
    }
  };

  const handleApproveTask = async (taskId) => {
    try {
      await adminAxios.post(`/command-center/tasks/${taskId}/approve`);
      toast.success("Tarefa aprovada");
      setSelectedTask(null);
      await fetchTasks();
    } catch (err) {
      toast.error("Erro ao aprovar tarefa");
    }
  };

  const handleDismissTask = async (taskId) => {
    try {
      await adminAxios.post(`/command-center/tasks/${taskId}/dismiss`);
      toast.success("Tarefa descartada");
      setSelectedTask(null);
      await fetchTasks();
    } catch (err) {
      toast.error("Erro ao descartar tarefa");
    }
  };

  const handleAnalyzeTask = async (taskId) => {
    try {
      toast.info("Analisando com GPT-4.1...", { duration: 5000 });
      const res = await adminAxios.post(`/command-center/tasks/${taskId}/analyze-llm`);
      
      if (res.data.success) {
        setSelectedTask({ task: res.data.task, logs: [] });
        toast.success(
          `Analise concluida (${Math.round(res.data.analysis.confidence * 100)}% confianca)`,
          { duration: 4000 }
        );
        
        if (res.data.suggestion?.action && res.data.suggestion.action !== "manual_review") {
          toast.info(`Sugestao: ${res.data.suggestion.action} - ${res.data.suggestion.reason}`, { duration: 6000 });
        }
      }
      
      await fetchTasks();
    } catch (err) {
      console.error("LLM analysis error:", err);
      toast.error(err.response?.data?.detail || "Erro na analise LLM");
    }
  };

  const handleExecuteTask = async (taskId) => {
    try {
      toast.info("Executando acao...");
      const res = await adminAxios.post(`/command-center/tasks/${taskId}/execute`);
      if (res.data.success) {
        toast.success("Acao executada com sucesso");
      } else {
        toast.error(`Falha: ${res.data.execution?.error || "Erro desconhecido"}`);
      }
      setSelectedTask(null);
      await fetchTasks();
    } catch (err) {
      toast.error("Erro ao executar tarefa");
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await adminAxios.post(`/command-center/alerts/${alertId}/acknowledge`);
      toast.success("Alerta reconhecido");
      await fetchAlerts();
    } catch (err) {
      toast.error("Erro ao reconhecer alerta");
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await adminAxios.post(`/command-center/alerts/${alertId}/resolve`);
      toast.success("Alerta resolvido");
      await fetchAlerts();
    } catch (err) {
      toast.error("Erro ao resolver alerta");
    }
  };

  const handleCheckAlerts = async () => {
    try {
      toast.info("Verificando alertas...");
      const res = await adminAxios.post("/command-center/alerts/check");
      if (res.data.new_alerts_count > 0) {
        toast.success(`${res.data.new_alerts_count} novos alertas gerados`);
      } else {
        toast.info("Nenhum novo alerta detectado");
      }
      await fetchAlerts();
    } catch (err) {
      toast.error("Erro ao verificar alertas");
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <Bot className="w-12 h-12 mx-auto text-primary animate-pulse" />
          <p className="text-muted-foreground">Carregando AI Command Center...</p>
        </div>
      </div>
    );
  }

  const agents = dashboard?.agents || {};
  const taskStats = dashboard?.tasks || {};
  const jarvis = dashboard?.jarvis || {};
  const criticalTasks = dashboard?.critical_tasks || [];

  return (
    <div className="space-y-6" data-testid="ai-command-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="command-center-title">
            <Bot className="w-8 h-8 text-primary" />
            AI Command Center
          </h1>
          <p className="text-muted-foreground mt-1">
            Central de agentes de IA para monitoramento e automacao operacional
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button onClick={() => handleRunScan()} data-testid="scan-btn">
            <Play className="w-4 h-4 mr-2" />
            Executar Scan
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-card border-border/50">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold" data-testid="stat-total">{taskStats.total || 0}</p>
            <p className="text-xs text-muted-foreground">Tarefas Hoje</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-red-400" data-testid="stat-critical">{taskStats.critical || 0}</p>
            <p className="text-xs text-muted-foreground">Criticas</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-green-400" data-testid="stat-completed">{taskStats.completed || 0}</p>
            <p className="text-xs text-muted-foreground">Concluidas</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-amber-400" data-testid="stat-failed">{taskStats.failed || 0}</p>
            <p className="text-xs text-muted-foreground">Falhas</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold" data-testid="stat-restaurants">{taskStats.restaurants_impacted || 0}</p>
            <p className="text-xs text-muted-foreground">Restaurantes</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Visao Geral</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tarefas</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Alertas</TabsTrigger>
          <TabsTrigger value="auto-repair" data-testid="tab-auto-repair">
            <HeartPulse className="w-4 h-4 mr-1" />
            Auto-Repair
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <TrendingUp className="w-4 h-4 mr-1" />
            Historico
          </TabsTrigger>
          <TabsTrigger value="metrics" data-testid="tab-metrics">Metricas</TabsTrigger>
          <TabsTrigger value="jarvis" data-testid="tab-jarvis">Jarvis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <OverviewSection 
            agents={agents}
            jarvis={jarvis}
            criticalTasks={criticalTasks}
            onViewTask={handleViewTask}
          />
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <TasksSection 
            tasks={tasks}
            agents={agents}
            filters={filters}
            setFilters={setFilters}
            onFilter={fetchTasks}
            onViewTask={handleViewTask}
          />
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          <AlertsDashboard 
            alerts={alerts}
            agents={agents}
            onAcknowledge={handleAcknowledgeAlert}
            onResolve={handleResolveAlert}
            onCheckAlerts={handleCheckAlerts}
          />
        </TabsContent>

        {/* Metrics Tab */}
        <TabsContent value="metrics" className="space-y-6">
          <MetricsDashboard metrics={metrics} agents={agents} />
        </TabsContent>

        {/* Jarvis Tab */}
        <TabsContent value="jarvis" className="space-y-4">
          <JarvisDetails adminAxios={adminAxios} jarvis={jarvis} />
        </TabsContent>

        {/* Auto-Repair Tab */}
        <TabsContent value="auto-repair" className="space-y-6">
          <AutoRepairSection 
            stats={autoRepairStats} 
            attempts={autoRepairAttempts}
            onRefresh={async () => {
              await fetchAutoRepairStats();
              await fetchAutoRepairAttempts();
            }}
          />
        </TabsContent>

        {/* History Dashboard Tab */}
        <TabsContent value="history" className="space-y-6">
          <AutoRepairHistoryDashboard 
            history={autoRepairHistory}
            period={historyPeriod}
            onPeriodChange={async (newPeriod) => {
              setHistoryPeriod(newPeriod);
              await fetchAutoRepairHistory(newPeriod);
            }}
            onRefresh={() => fetchAutoRepairHistory(historyPeriod)}
          />
        </TabsContent>
      </Tabs>

      {/* Task Detail Dialog */}
      {selectedTask && (
        <TaskDetailDialog
          task={selectedTask.task}
          logs={selectedTask.logs}
          onClose={() => setSelectedTask(null)}
          onApprove={handleApproveTask}
          onDismiss={handleDismissTask}
          onAnalyze={handleAnalyzeTask}
          onExecute={handleExecuteTask}
          adminAxios={adminAxios}
        />
      )}
    </div>
  );
}

// ============================================================================
// INLINE SUB-COMPONENTS (smaller, kept in same file for simplicity)
// ============================================================================

function OverviewSection({ agents, jarvis, criticalTasks, onViewTask }) {
  return (
    <>
      {/* Agent Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(agents).map(([id, agent]) => {
          const Icon = AgentIcons[id] || Bot;
          const avatar = AgentAvatars[id] || "🤖";
          const status = agent.status || {};
          const mode = ModeLabels[status.mode] || ModeLabels.observer;
          const ModeIcon = mode.icon;
          
          return (
            <Card 
              key={id} 
              className="bg-card border-border/50 hover:border-primary/30 transition-colors"
              data-testid={`agent-card-${id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${agent.color}20` }}
                    >
                      {avatar}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {agent.name || id}
                        <Icon className="w-4 h-4" style={{ color: agent.color }} />
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">{agent.role}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Circle 
                          className={`w-2 h-2 fill-current ${status.health === 'healthy' ? 'text-green-400' : 'text-red-400'}`} 
                        />
                        <span className="text-xs text-muted-foreground">
                          {status.enabled ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className={mode.color}>
                    <ModeIcon className="w-3 h-3 mr-1" />
                    {mode.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                  {agent.description}
                </p>
                <div className="grid grid-cols-2 gap-4 text-center border-t border-border/50 pt-4">
                  <div>
                    <p className="text-2xl font-bold">{status.tasks_open || 0}</p>
                    <p className="text-xs text-muted-foreground">Abertas</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400">{status.tasks_completed_today || 0}</p>
                    <p className="text-xs text-muted-foreground">Hoje</p>
                  </div>
                </div>
                {status.last_error && (
                  <div className="mt-3 p-2 bg-red-500/10 rounded text-xs text-red-400">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    {status.last_error}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Jarvis Status Card */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <Bot className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Jarvis</CardTitle>
                <CardDescription>
                  Sistema de auto-remediacao 
                  {jarvis.supervisor && (
                    <span className="ml-1">
                      • Supervisionado por <span className="text-primary">{jarvis.supervisor.name}</span>
                    </span>
                  )}
                </CardDescription>
              </div>
            </div>
            <Badge variant={jarvis.enabled ? "default" : "destructive"}>
              {jarvis.enabled ? "Online" : "Offline"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold">{jarvis.stats?.scans_today || 0}</p>
              <p className="text-xs text-muted-foreground">Scans Hoje</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{jarvis.stats?.incidents_today || 0}</p>
              <p className="text-xs text-muted-foreground">Incidents</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{jarvis.stats?.fixes_today || 0}</p>
              <p className="text-xs text-muted-foreground">Correcoes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{jarvis.stats?.failures_today || 0}</p>
              <p className="text-xs text-muted-foreground">Falhas</p>
            </div>
          </div>
          {jarvis.circuit_breakers_tripped?.length > 0 && (
            <div className="mt-4 p-3 bg-amber-500/10 rounded-lg">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Circuit Breakers Ativos: {jarvis.circuit_breakers_tripped.join(", ")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Critical Tasks */}
      {criticalTasks.length > 0 && (
        <Card className="bg-card border-border/50 border-red-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Tarefas Criticas ({criticalTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {criticalTasks.map((task) => (
              <div 
                key={task.task_id}
                className="flex items-center justify-between p-3 bg-red-500/5 rounded-lg border border-red-500/20 hover:bg-red-500/10 cursor-pointer transition-colors"
                onClick={() => onViewTask(task.task_id)}
                data-testid={`critical-task-${task.task_id}`}
              >
                <div className="flex items-center gap-3">
                  <Badge className={PriorityColors[task.priority]}>
                    {task.agent.toUpperCase()}
                  </Badge>
                  <div>
                    <p className="font-medium">{task.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {task.restaurant_name || "Global"} • {new Date(task.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function TasksSection({ tasks, agents, filters, setFilters, onFilter, onViewTask }) {
  return (
    <>
      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <Select value={filters.agent || "all"} onValueChange={(v) => setFilters({...filters, agent: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[150px]" data-testid="filter-agent">
            <SelectValue placeholder="Agente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="tech">Tech</SelectItem>
            <SelectItem value="operations">Operations</SelectItem>
            <SelectItem value="finance">Finance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="analyzing">Analisando</SelectItem>
            <SelectItem value="suggested">Sugerido</SelectItem>
            <SelectItem value="executed">Executado</SelectItem>
            <SelectItem value="failed">Falhou</SelectItem>
            <SelectItem value="dismissed">Descartado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({...filters, priority: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[150px]" data-testid="filter-priority">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="critical">Critica</SelectItem>
            <SelectItem value="high">Alta</SelectItem>
            <SelectItem value="medium">Media</SelectItem>
            <SelectItem value="low">Baixa</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={onFilter} size="sm">
          <Filter className="w-4 h-4 mr-2" />
          Filtrar
        </Button>
      </div>

      {/* Tasks List */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tasks-table">
              <thead>
                <tr className="border-b border-border/50 text-left text-sm text-muted-foreground">
                  <th className="p-3 font-medium">ID</th>
                  <th className="p-3 font-medium">Agente</th>
                  <th className="p-3 font-medium">Titulo</th>
                  <th className="p-3 font-medium">Prioridade</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Restaurante</th>
                  <th className="p-3 font-medium">Criado</th>
                  <th className="p-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const agentDef = agents[task.agent] || {};
                  const avatar = AgentAvatars[task.agent] || "🤖";
                  
                  return (
                    <tr 
                      key={task.task_id} 
                      className="border-b border-border/20 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => onViewTask(task.task_id)}
                      data-testid={`task-row-${task.task_id}`}
                    >
                      <td className="p-3 font-mono text-xs">{task.task_id.slice(0, 8)}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="gap-1">
                          <span>{avatar}</span>
                          {agentDef.name || task.agent}
                        </Badge>
                      </td>
                      <td className="p-3 max-w-[250px] truncate">{task.title}</td>
                      <td className="p-3">
                        <Badge className={PriorityColors[task.priority]}>{task.priority}</Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={StatusColors[task.status]}>{task.status}</Badge>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{task.restaurant_name || "—"}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {new Date(task.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </td>
                    </tr>
                  );
                })}
                {tasks.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      Nenhuma tarefa encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
